import React, { useState } from 'react';
import { Flame, TrendingUp, Calendar, Award, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMissions } from '../hooks/useMissions';
import MissionCard from './MissionCard';
import ImpactStats from './ImpactStats';
import LoadingSpinner from './LoadingSpinner';
import IssueReportButton from './IssueReportButton';

export default function Dashboard() {
  const { profile } = useAuth();
  const { missions, loading } = useMissions();
  const [showIssueScanner, setShowIssueScanner] = useState(false);

  if (!profile) {
    return <LoadingSpinner />;
  }

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold mb-2">Welcome back, {profile.username}!</h2>
              <p className="text-emerald-100 text-lg">Ready to make a positive impact today?</p>
            </div>
            <div className="hidden sm:block">
              <img
                src={profile.avatar_url}
                alt={profile.username}
                className="w-20 h-20 rounded-full border-4 border-white/20"
              />
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Flame className="h-5 w-5 text-orange-300" />
                <span className="text-sm font-medium">Streak</span>
              </div>
              <p className="text-2xl font-bold">{profile.streak} days</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="h-5 w-5 text-blue-300" />
                <span className="text-sm font-medium">Level</span>
              </div>
              <p className="text-2xl font-bold">{profile.level}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
                <span className="text-sm font-medium">EcoCoins</span>
              </div>
              <p className="text-2xl font-bold">{profile.eco_coins.toLocaleString()}</p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="h-5 w-5 text-purple-300" />
                <span className="text-sm font-medium">Missions</span>
              </div>
              <p className="text-2xl font-bold">{missions.filter(m => m.completed).length}</p>
            </div>
          </div>
        </div>

        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading missions...</p>
        </div>
      </div>
    );
  }

  const todaysMissions = missions.filter(m => m.time_frame === 'daily' && !m.completed).slice(0, 3);
  const weeklyMissions = missions.filter(m => m.time_frame === 'weekly' && !m.completed).slice(0, 2);

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold mb-2">Welcome back, {profile.username}!</h2>
            <p className="text-emerald-100 text-lg">Ready to make a positive impact today?</p>
          </div>
          <div className="hidden sm:block">
            <img
              src={profile.avatar_url}
              alt={profile.username}
              className="w-20 h-20 rounded-full border-4 border-white/20"
            />
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Flame className="h-5 w-5 text-orange-300" />
              <span className="text-sm font-medium">Streak</span>
            </div>
            <p className="text-2xl font-bold">{profile.streak} days</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="h-5 w-5 text-blue-300" />
              <span className="text-sm font-medium">Level</span>
            </div>
            <p className="text-2xl font-bold">{profile.level}</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-yellow-300 rounded-full"></div>
              <span className="text-sm font-medium">EcoCoins</span>
            </div>
            <p className="text-2xl font-bold">{profile.eco_coins.toLocaleString()}</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Award className="h-5 w-5 text-purple-300" />
              <span className="text-sm font-medium">Missions</span>
            </div>
            <p className="text-2xl font-bold">{missions.filter(m => m.completed).length}</p>
          </div>
        </div>
      </div>

      {/* Impact Stats - This component now uses filtered active impact types */}
      <ImpactStats />

      {/* Environmental Issue Scanner Promo */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="bg-white p-4 rounded-xl shadow-md">
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertTriangle className="h-10 w-10 text-red-600" />
            </div>
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Spotted an Environmental Issue?
            </h3>
            <p className="text-gray-600 mb-4">
              Help protect our planet by reporting environmental issues in your community. 
              Your reports will be verified and sent to local authorities.
            </p>
            
            <IssueReportButton />
          </div>
        </div>
      </div>

      {/* Today's Missions */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-emerald-600" />
            <h3 className="text-2xl font-bold text-gray-900">Today's Missions</h3>
          </div>
          <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
            {todaysMissions.length} available
          </span>
        </div>
        
        {todaysMissions.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {todaysMissions.map((mission) => (
              <MissionCard key={mission.id} mission={mission} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600">All daily missions completed! Check back tomorrow for new challenges.</p>
          </div>
        )}
      </div>

      {/* Weekly Challenges */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <TrendingUp className="h-6 w-6 text-blue-600" />
            <h3 className="text-2xl font-bold text-gray-900">Weekly Challenges</h3>
          </div>
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Higher Rewards
          </span>
        </div>
        
        {weeklyMissions.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2">
            {weeklyMissions.map((mission) => (
              <MissionCard key={mission.id} mission={mission} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
            <p className="text-gray-600">All weekly challenges completed! Great job!</p>
          </div>
        )}
      </div>
    </div>
  );
}